"use client";

import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import { Trash2, GripVertical, Download, Loader2, CheckCircle2, AlertCircle, Save, Play, Plus, X, Maximize2, Image as ImageIcon, Clock } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Menu, Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { TimelineItem as TimelineItemInterface } from '@/types/timeline-item.interface';

interface TimelineItemProps extends TimelineItemInterface {
    isLast?: boolean;
    onPreview?: (segment: any) => void;
    onUploadImage?: (segmentId: number, e: React.ChangeEvent<HTMLInputElement>) => void;
    onDeleteImage?: (imageId: number) => void;
    onDeleteVideo?: (segmentId: number) => void;
    isUploadingImage?: boolean;
    index: number;
}

/**
 * 自适应高度的 Textarea 组件
 */
const AutoExpandingTextarea = ({
    value,
    onChange,
    placeholder,
    className,
    maxLines = 10
}: {
    value: string;
    onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    placeholder: string;
    className?: string;
    maxLines?: number;
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const adjustHeight = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            const newHeight = Math.min(textarea.scrollHeight, maxLines * 24 + 16);
            textarea.style.height = `${newHeight}px`;
        }
    };

    useEffect(() => { adjustHeight(); }, [value]);

    return (
        <textarea
            ref={textareaRef}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className={`w-full resize-none overflow-y-auto outline-none transition-all
  [&::-webkit-scrollbar]:w-2
  [&::-webkit-scrollbar-track]:bg-dark-500
  [&::-webkit-scrollbar-thumb]:bg-dark-200
  dark:[&::-webkit-scrollbar-track]:bg-dark-500
  dark:[&::-webkit-scrollbar-thumb]:bg-dark-200 ${className}`}
            rows={1}
        />
    );
};

export const TimelineItem = React.memo(({
    segment,
    onUpdate,
    onDelete,
    onDownload,
    isLast,
    onPreview,
    onUploadImage,
    onDeleteImage,
    onDeleteVideo,
    isUploadingImage,
    index
}: TimelineItemProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: segment.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 50 : undefined,
        opacity: isDragging ? 0.5 : undefined,
    };

    const [localDesc, setLocalDesc] = useState(segment.desc || '');
    const [localPrompt, setLocalPrompt] = useState(segment.prompt || '');
    const [localNegPrompt, setLocalNegPrompt] = useState(segment.negativePrompt || '');
    const [localExtInstr, setLocalExtInstr] = useState(segment.extensionInstruction || '');
    // const [localExtInstr, setLocalExtInstr] = useState(segment.extensionInstruction || '');
    const [selectedZoomImage, setSelectedZoomImage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => { setLocalDesc(segment.desc || ""); }, [segment.desc]);
    useEffect(() => { setLocalPrompt(segment.prompt || ""); }, [segment.prompt]);
    useEffect(() => { setLocalNegPrompt(segment.negativePrompt || ""); }, [segment.negativePrompt]);
    useEffect(() => { setLocalExtInstr(segment.extensionInstruction || ""); }, [segment.extensionInstruction]);

    const hasChanges = localDesc !== (segment.desc || "") ||
        localPrompt !== (segment.prompt || "") ||
        localNegPrompt !== (segment.negativePrompt || "") ||
        localExtInstr !== (segment.extensionInstruction || "");

    const handleSave = () => {
        onUpdate(segment.id, {
            desc: localDesc,
            prompt: localPrompt,
            negativePrompt: localNegPrompt,
            extensionInstruction: localExtInstr
        });
    };

    const renderStatusBadge = () => {
        const baseClass = "flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all border whitespace-nowrap shrink-0";

        switch (segment.status) {
            case "processing":
                return (
                    <div className={`${baseClass} bg-blue-500/10 text-blue-500 border-blue-500/20`}>
                        <Loader2 size={10} className="animate-spin" />
                        <span>处理中</span>
                    </div>
                );
            case "completed":
                return (
                    <Menu as="div" className="relative inline-block text-left">
                        {({ open }) => (
                            <>
                                <Menu.Button
                                    className={`${baseClass} bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20`}
                                >
                                    <CheckCircle2 size={10} />
                                    <span>生成完毕</span>
                                    <Play size={8} className={`transition-transform duration-200 ${open ? 'rotate-90' : ''}`} fill="currentColor" />
                                </Menu.Button>

                                <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-200"
                                    enterFrom="opacity-0 scale-95"
                                    enterTo="opacity-100 scale-100"
                                    leave="transition ease-in duration-150"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <Menu.Items className="absolute right-0 top-full mt-2 w-72 bg-zinc-900 border border-zinc-800 rounded-none z-100 p-4 text-white focus:outline-none">
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between border-b border-zinc-100 dark:border-zinc-800 pb-2">
                                                <h4 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">视频详情</h4>
                                            </div>

                                            <div className="aspect-video rounded-none overflow-hidden bg-black border border-zinc-800">
                                                <video src={segment.videoUrl || undefined} className="w-full h-full object-contain" controls preload="metadata" />
                                            </div>

                                            <div className="space-y-2 text-[10px]">
                                                <div className="flex justify-between items-center text-zinc-500">
                                                    <span>文件名:</span>
                                                    <span className="text-zinc-300 font-mono truncate max-w-[160px]">{segment.videoKey}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-zinc-500">
                                                    <span>生成时间:</span>
                                                    <span className="text-zinc-300">
                                                        {segment.updatedAt ? new Date(segment.updatedAt).toLocaleString() : '未知'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-2 pt-2">
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <button
                                                            onClick={(e) => onDownload?.(segment.videoKey || "", e)}
                                                            className={`flex items-center justify-center gap-2 py-2 text-white rounded-lg font-bold text-[10px] transition-colors ${active ? 'bg-blue-500' : 'bg-blue-600'}`}
                                                        >
                                                            <Download size={12} /> 下载视频
                                                        </button>
                                                    )}
                                                </Menu.Item>
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <button
                                                            onClick={() => onDeleteVideo?.(segment.id)}
                                                            className={`flex items-center justify-center gap-2 py-2 text-red-500 rounded-lg font-bold text-[10px] transition-colors border border-red-500/20 ${active ? 'bg-red-500/20' : 'bg-red-500/10'}`}
                                                        >
                                                            <Trash2 size={12} /> 清除资产
                                                        </button>
                                                    )}
                                                </Menu.Item>
                                            </div>
                                        </div>
                                    </Menu.Items>
                                </Transition>
                            </>
                        )}
                    </Menu>
                );
            case "error":
                return (
                    <div className={`${baseClass} bg-red-500/10 text-red-500 border-red-500/20`}>
                        <AlertCircle size={10} />
                        <span>生成失败</span>
                    </div>
                );
            default:
                return (
                    <div className={`${baseClass} bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-500 border-zinc-200 dark:border-zinc-700`}>
                        <Clock size={10} />
                        <span>待执行</span>
                    </div>
                );
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`group relative flex gap-4 transition-all ${isDragging ? "z-50" : "z-10"} mb-3`}
        >
            {/* Left Rail - Segment Index & Connection */}
            <div className="flex flex-col items-center w-8 shrink-0 py-2">
                <div className={`w-6 h-6 flex items-center justify-center rounded-none text-[10px] font-black border
                    ${index === 0 ? 'bg-blue-600 border-blue-500 text-white' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border-zinc-200 dark:border-zinc-700'}`}>
                    {index + 1}
                </div>
                {!isLast && (
                    <div className="flex-1 w-px bg-zinc-200 dark:bg-zinc-800 my-2" />
                )}
            </div>

            {/* Right Side - Content Card */}
            <div className="flex-1 bg-white dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-800 rounded-none overflow-hidden hover:border-zinc-300 dark:hover:border-zinc-700 transition-colors">

                {/* 1. HEADER: Title & Status */}
                <div className="flex items-center justify-between px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800/20 border-b border-zinc-100 dark:border-zinc-800/50">
                    <div className="flex items-center gap-3 flex-1 overflow-hidden">
                        <div
                            {...attributes}
                            {...listeners}
                            className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-zinc-400 hover:text-zinc-600 transition-colors"
                        >
                            <GripVertical size={14} />
                        </div>
                        <input
                            className="bg-transparent font-bold text-sm text-zinc-800 dark:text-zinc-100 outline-none w-full truncate placeholder:text-zinc-400"
                            value={localDesc}
                            onChange={(e) => setLocalDesc(e.target.value)}
                            placeholder="为这段脚本起个名字..."
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        {renderStatusBadge()}
                        <div className="flex items-center gap-1 border-l border-zinc-200 dark:border-zinc-800 ml-2 pl-2">
                            <button
                                onClick={handleSave}
                                disabled={!hasChanges}
                                className={`p-1.5 transition-all rounded-none ${!hasChanges ? "text-zinc-300 dark:text-zinc-700 cursor-not-allowed" : "text-emerald-500 hover:bg-emerald-500/10"}`}
                                title="保存修改"
                            >
                                <Save size={14} />
                            </button>
                            <button
                                onClick={() => onDelete(segment.id)}
                                className="text-zinc-400 hover:text-red-500 p-1.5 rounded-none hover:bg-red-500/10 transition-all"
                                title="移除片段"
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* 2. BODY: Input Area */}
                <div className="p-4 space-y-4">
                    <div className="grid grid-cols-12 gap-4">
                        {/* Positive Prompt - Priority 8/12 */}
                        <div className="col-span-12 md:col-span-8 space-y-1.5">
                            <div className="flex items-center justify-between font-black uppercase tracking-tighter text-[9px]">
                                <label className="text-blue-500/80">核心画面描述</label>
                                <span className="text-zinc-400">{localPrompt.length} chars</span>
                            </div>
                            <AutoExpandingTextarea
                                value={localPrompt}
                                onChange={(e) => setLocalPrompt(e.target.value)}
                                placeholder="描述具体的视觉细节、光影、构图..."
                                className="bg-zinc-50 dark:bg-zinc-800/30 text-xs text-zinc-800 dark:text-zinc-200 p-3 rounded-none border border-zinc-200 dark:border-zinc-700/50 focus:border-blue-500/50 min-h-[140px] transition-colors"
                            />
                        </div>

                        {/* Negative Prompt - Low Priority 4/12 */}
                        <div className="col-span-12 md:col-span-4 space-y-1.5">
                            <label className="text-[9px] text-red-500/80 uppercase tracking-tighter font-black">负向约束</label>
                            <AutoExpandingTextarea
                                value={localNegPrompt}
                                onChange={(e) => setLocalNegPrompt(e.target.value)}
                                placeholder="避免出现的元素..."
                                className="bg-zinc-100/30 dark:bg-zinc-900/40 text-[11px] text-zinc-500 p-3 rounded-none border border-zinc-200 dark:border-zinc-800/50 focus:border-red-500/30 min-h-[140px] transition-all"
                            />
                        </div>
                    </div>

                    {index > 0 && (
                        <div className="bg-zinc-50/50 dark:bg-zinc-800/10 p-3 border border-zinc-200 dark:border-zinc-800/50">
                            <label className="text-[9px] text-emerald-500/80 uppercase tracking-tighter font-black mb-1.5 block">延长段专用逻辑</label>
                            <AutoExpandingTextarea
                                value={localExtInstr}
                                onChange={(e) => setLocalExtInstr(e.target.value)}
                                placeholder="定义上一段与本段的转场或动作关系..."
                                className="bg-transparent text-[11px] text-zinc-500 dark:text-zinc-400 outline-none w-full"
                            />
                        </div>
                    )}
                </div>

                {/* 3. FOOTER: Assets Bar */}
                <div className="px-4 py-3 bg-zinc-50/50 dark:bg-zinc-900/40 border-t border-zinc-100 dark:border-zinc-800 flex items-center gap-6 overflow-x-auto custom-scrollbar">
                    {/* Reference Images Group */}
                    {index === 0 && (
                        <div className="flex items-center gap-3 shrink-0">
                            <div className="flex items-center gap-1.5 text-[10px] font-black text-indigo-400 uppercase">
                                <ImageIcon size={12} />
                                <span>参考图</span>
                            </div>
                            <div className="flex gap-1">
                                {segment.referenceImages?.map((img) => (
                                    <div key={img.id} className="relative w-16 h-16 rounded-none overflow-hidden border border-zinc-200 dark:border-zinc-700 cursor-pointer group/img">
                                        <img src={img.path} className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-110" alt="Ref" />
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center" onClick={() => setSelectedZoomImage(img.path)}>
                                            <button className="text-white outline-none bg-white/20 p-2 rounded-full hover:bg-white/30 transition-colors">
                                                <Maximize2 size={14} />
                                            </button>
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onDeleteImage?.(img.id); }}
                                            className="absolute top-0.5 right-0.5 p-1 bg-red-600/80 text-white opacity-0 group-hover/img:opacity-100 transition-all scale-90 hover:bg-red-600 outline-none"
                                        >
                                            <X size={10} />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isUploadingImage || (segment.referenceImages?.length || 0) >= 3}
                                    className="w-16 h-16 flex items-center justify-center bg-zinc-100 dark:bg-zinc-800 border border-dashed border-zinc-200 dark:border-zinc-700 text-zinc-400 hover:text-indigo-500 transition-colors disabled:opacity-20 outline-none"
                                >
                                    {isUploadingImage ? <Loader2 size={14} className="animate-spin" /> : <Plus size={16} />}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Timeline Position Info (Visual only) */}
                    <div className="flex items-center gap-2 ml-auto shrink-0 border-l border-zinc-200 dark:border-zinc-800 pl-4 text-[10px] text-zinc-400 font-bold uppercase italic">
                        <Clock size={12} className="opacity-50" />
                        <span>Seq #{index + 1}</span>
                    </div>
                </div>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*"
                onChange={(e) => onUploadImage?.(segment.id, e)}
            />

            {/* Lightbox */}
            <Transition.Root show={!!selectedZoomImage} as={Fragment}>
                <Dialog as="div" className="relative z-500" onClose={() => setSelectedZoomImage(null)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/95 backdrop-blur-md" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-8 text-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative max-w-5xl max-h-[90vh] flex flex-col items-center">
                                    <img
                                        src={selectedZoomImage || ''}
                                        className="max-w-full max-h-full object-contain border border-white/10"
                                        alt="Zoom"
                                    />
                                    <button
                                        onClick={() => setSelectedZoomImage(null)}
                                        className="mt-4 px-6 py-2 bg-white/10 hover:bg-red-600 text-white font-bold text-xs uppercase tracking-widest border border-white/20 transition-all outline-none"
                                    >
                                        关闭预览
                                    </button>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </div>
    );
});

TimelineItem.displayName = "TimelineItem";
